// ──────────────────────────────────────────────
// classifyStartError — classifies recording start failures for
// Statsig tracking and picks the user-facing recovery message.
// ──────────────────────────────────────────────

import {
  MIC_CONTENTION_MESSAGE,
  MicrophoneAcquireTimeoutError,
} from '@services/audio/getUserMediaWithTimeout';

import { MixerResumeTimeoutError } from '../../lib/recorder/acquireRecordingStream';

export type StartErrorType =
  | 'not_ready'
  | 'recording_service_state_error'
  | 'api_session_failed'
  | 'media_recorder_failed'
  | 'web_lock_unavailable'
  | 'mime_type_unsupported'
  | 'mic_permission_denied'
  | 'mic_not_found'
  | 'mic_not_readable'
  | 'mic_acquire_timeout'
  | 'mixer_resume_timeout'
  | 'unknown';

/** Classify recording start errors for Statsig tracking. */
export function classifyStartError(err: unknown): StartErrorType {
  if (err instanceof MicrophoneAcquireTimeoutError) return 'mic_acquire_timeout';
  if (err instanceof MixerResumeTimeoutError) return 'mixer_resume_timeout';
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes('Not ready to record')) return 'not_ready';
  if (msg.includes('Cannot start recording in state:')) return 'recording_service_state_error';
  if (msg.includes('Failed to get transcription session ID')) return 'api_session_failed';
  // RecordingService.start() rejections — the service is already in 'error' state.
  if (msg.includes('Failed to start MediaRecorder')) return 'media_recorder_failed';
  if (msg.includes('Web Lock held')) return 'web_lock_unavailable';
  if (msg.includes('No supported audio MIME type')) return 'mime_type_unsupported';
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError') return 'mic_permission_denied';
    if (err.name === 'NotFoundError') return 'mic_not_found';
    if (err.name === 'NotReadableError') return 'mic_not_readable';
    // Firefox reports a busy device as "AbortError: Starting audio failed".
    if (err.name === 'AbortError') return 'mic_not_readable';
  }
  return 'unknown';
}

const GENERIC_START_ERROR_MESSAGE =
  'Failed to start recording. Check audio settings and try again.';

/**
 * Device contention — a mic held by another tab or app, or one the OS never
 * handed over — has a concrete fix the clinician can apply before retrying.
 * Everything else keeps the generic message.
 */
/** A suspended mixer needs a fresh user gesture; a reload is the reliable way to get one. */
const MIXER_RESUME_MESSAGE = "Couldn't start audio processing. Reload this tab and try again.";

/** The cross-tab Web Lock is held: the fix is in the other tab, not in audio settings. */
const WEB_LOCK_MESSAGE =
  'Recording is already running in another tab. Stop it there, then try again.';

const RECOVERY_MESSAGES: Partial<Record<StartErrorType, string>> = {
  mic_acquire_timeout: MIC_CONTENTION_MESSAGE,
  mic_not_readable: MIC_CONTENTION_MESSAGE,
  mixer_resume_timeout: MIXER_RESUME_MESSAGE,
  web_lock_unavailable: WEB_LOCK_MESSAGE,
};

/** User-facing message for a recording start failure. */
export function startErrorMessage(err: unknown): string {
  return RECOVERY_MESSAGES[classifyStartError(err)] ?? GENERIC_START_ERROR_MESSAGE;
}
